import axios from 'axios';
import moment from 'moment';
import { getAuthHeaders } from '../auth/authClient';

/**
 * axios-instance holder.
 *
 * The **authenticated** user session (ropc / oauth2 / microsoft) is now
 * owned by the `@vulog/auth` Service Worker (reached via
 * `src/auth/authClient`): it injects `Authorization: Bearer` and refreshes
 * transparently on the API base URL. The authenticated client here keeps
 * only its axios instance — no Bearer and no `X-API-Key`, since the SW injects
 * both (it is constructed with `withApiKey: false`; see below). In the page
 * runtime (no Service Worker API, e.g. the OneApp's iOS webview), `@vulog/auth`
 * runs in the page and the authenticated client's interceptors add both headers
 * from `getAuthHeaders` instead; see `AuthenticatedOpenIDClient`.
 *
 * The **anonymous** client (`client_credentials`) is a separate concern
 * the SW does not manage, so it retains its own token fetch + header for
 * the app's public/anonymous endpoints, fetching the token on demand.
 */
class BaseOpenIDClient {
    openIDConf = null;

    baseURL = null;

    instanceAxios = null;

    constructor(openIDConf, baseURL = null, { withApiKey = true } = {}) {
        this.openIDConf = openIDConf;
        this.baseURL = baseURL;
        this.instanceAxios = axios.create({
            timeout: 30000,
            headers: {
                'Cache-Control': 'no-cache',
                // The authenticated client omits X-API-Key: the Service Worker
                // injects it (and the Bearer) on the intercepted API base URL,
                // or, in the page runtime, `AuthenticatedOpenIDClient`'s
                // request interceptor does.
                ...(withApiKey ? { 'X-API-Key': openIDConf.apiKey } : {}),
                'Content-Type': 'application/json',
            },
            withCredentials: false,
        });
    }
}

/**
 * Marks the anonymous client's own token request, so its request interceptor
 * lets it through instead of waiting on a token. A plain string key, for the
 * same `mergeConfig` reason as `AUTH_RETRIED` below.
 */
const TOKEN_REQUEST = '__anonymousTokenRequest';

/** A token this close to its expiry is renewed first, so it does not lapse in flight. */
const EXPIRY_MARGIN_SECONDS = 30;

/**
 * Anonymous / sign-up client: `client_credentials` grant, self-managed
 * (not routed through the Service Worker, which only owns user sessions).
 *
 * Its token is fetched on demand: a request sent without a valid token first
 * gets one, so a public page reached in a fresh session (password reset,
 * sign-up) does not go out bare and come back 401. If the token cannot be
 * obtained, the request is not sent and rejects with the token request's
 * error. "Valid" is judged by the local clock only: there is no renew-and-retry
 * after a 401, so a token the server rejects early fails the call.
 */
class AnonymousOpenIDClient extends BaseOpenIDClient {
    accessToken = null;

    /** The moment the token expires (despite the name), or null. */
    expiresIn = null;

    /** The token request in flight, shared by concurrent requests; null when none. */
    pendingToken = null;

    constructor(...args) {
        super(...args);
        // The Bearer is set here on every request, never in `defaults`: axios
        // merges `defaults` into the config before interceptors run, so a token
        // that arrives in between would miss a request relying on them.
        this.instanceAxios.interceptors.request.use(async config => {
            if (config[TOKEN_REQUEST]) return config;
            if (!this.isAuthenticate()) await this.ensureToken();
            return { ...config, headers: { ...config.headers, Authorization: `Bearer ${this.accessToken}` } };
        });
    }

    /**
     * POST the token request and store the token it returns.
     *
     * @private Call `ensureToken`, which shares a request already in flight.
     * @param {URLSearchParams} params The form body of the token request.
     * @returns {Promise<string>} Resolves 'OK'; rejects if the request fails or the response holds no usable token.
     */
    postData = params =>
        this.instanceAxios
            .request({
                method: 'POST',
                url: this.baseURL,
                data: params,
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                [TOKEN_REQUEST]: true,
            })
            .then(({ data }) => {
                const { access_token: accessToken, expires_in: rawExpiresIn } = data || {};
                // Number(): some IdPs send `expires_in` as a numeric string.
                const expiresIn =
                    typeof rawExpiresIn === 'string' && rawExpiresIn.trim() ? Number(rawExpiresIn) : rawExpiresIn;
                if (typeof accessToken !== 'string' || !accessToken || !Number.isFinite(expiresIn)) {
                    throw new Error('Anonymous token response has no access_token or expires_in');
                }
                this.accessToken = accessToken;
                this.expiresIn = moment().add(expiresIn, 'seconds');
                return 'OK';
            });

    /**
     * Request a `client_credentials` token.
     *
     * @private Call `ensureToken`, which shares a request already in flight.
     * @returns {Promise<string>} See `postData`.
     */
    fetchToken = () => {
        const params = new URLSearchParams();
        params.append('client_id', this.openIDConf.clientId);
        params.append('client_secret', this.openIDConf.clientSecret);
        params.append('securityOptions', 'SSL_OP_NO_SSLv3');
        params.append('grant_type', 'client_credentials');
        return this.postData(params);
    };

    /**
     * Fetch a token, joining the request already in flight if there is one.
     * Does not check the current token: callers check `isAuthenticate()` first.
     *
     * @returns {Promise<string>} Resolves 'OK' once a token is set; rejects if the token request fails.
     */
    ensureToken = () => {
        if (!this.pendingToken) {
            this.pendingToken = this.fetchToken().finally(() => {
                this.pendingToken = null;
            });
        }
        return this.pendingToken;
    };

    isAuthenticate = () =>
        !!(
            this.accessToken &&
            this.expiresIn &&
            this.expiresIn.isAfter(moment().add(EXPIRY_MARGIN_SECONDS, 'seconds'))
        );
}

/**
 * Marks a request already replayed once after a 401, so a second 401 is final
 * and the replay skips the request interceptor (the renewal already supplied
 * its headers). A plain string key, not a `Symbol`: axios 0.33's `mergeConfig`
 * copies unknown string keys onto the replayed config but drops symbol keys.
 */
const AUTH_RETRIED = '__authRetried';

/**
 * Reasons that mean the user really is signed out (or cannot be signed in at
 * all), and so may go down the app's `{ code: 401 }` → logout path. Every other
 * reason — `renewal-unavailable`, `runtime-error`, and any reason a later
 * `@vulog/auth` adds — keeps the session: a stalled IndexedDB or a chunk that
 * failed to load on a flaky network must not sign the user out and wipe a
 * refresh token that is still valid.
 */
const SIGN_OUT_REASONS = ['session-lost', 'no-runtime'];

/**
 * @typedef {Error & {
 *   config: object,
 *   cause: Error,
 *   authReason: string,
 *   authCode: (string|null),
 *   request?: object,
 *   response?: { status: 401, data: string, config: object },
 * }} AxiosLikeAuthError
 * An `AuthUnavailableError` reshaped for `ApiClient.customReject`: exactly one
 * of `response` (a 401) or `request` (no response) is set.
 */

/**
 * Turn the package's `AuthUnavailableError` into an axios error
 * `ApiClient.customReject` already knows how to map. That error means the
 * request needs credentials it cannot get, so it is not sent — the Service
 * Worker would not send it bare either.
 *
 * - `session-lost` / `no-runtime`: a 401, so the existing `{ code: 401 }`
 *   handling signs the user out.
 * - Anything else (`renewal-unavailable`, `runtime-error`): shaped as "no
 *   response received", so callers show an error and do **not** log the user
 *   out (their 401 branch does). The session is kept.
 *
 * The converted error is a plain `Error` (its `name` says nothing) and the Bug
 * Logger only reads `name` / `message` / `stack` plus what `toJSON()` returns.
 * So `authReason` / `authCode` are kept on it, `toJSON()` gives `logError` the
 * same `{ code, status, config }` an axios error would (`code` is
 * `auth:<reason>[:<code>]`), and a `runtime-error`'s message carries its
 * cause's — the real IndexedDB or chunk-load failure.
 *
 * Matched by `name` and `status`, as the package's own `isAuthUnavailableError`
 * does (`instanceof` fails across copies of the package). It is not imported
 * from `@vulog/auth` so that `authClient` stays the only module that depends on
 * the package.
 *
 * @param {Error} error The `getAuthHeaders` rejection.
 * @param {object} config The axios request config.
 * @returns {(AxiosLikeAuthError|Error)} The reshaped error, or `error` unchanged when it is not an `AuthUnavailableError`.
 */
const toAxiosError = (error, config) => {
    if (!error || error.name !== 'AuthUnavailableError' || error.status !== 401) return error;
    const cause = error.cause && error.cause.message ? ` (${error.cause.message})` : '';
    const converted = new Error(`${error.message}${cause}`);
    converted.config = config;
    converted.cause = error;
    converted.authReason = error.reason;
    converted.authCode = error.code === undefined ? null : error.code;
    converted.toJSON = () => ({
        code: `auth:${error.reason}${converted.authCode ? `:${converted.authCode}` : ''}`,
        status: converted.response ? 401 : undefined,
        config: { method: config && config.method, url: config && config.url },
    });
    if (SIGN_OUT_REASONS.indexOf(error.reason) !== -1) {
        converted.response = { status: 401, data: 'Unauthorised', config };
    } else {
        converted.request = {};
    }
    return converted;
};

/**
 * The Bearer on `config` — only this client's request interceptor sets one on
 * the authenticated instance — without the scheme; `null` when there is none
 * or it is empty (the package rejects an empty `rejectedToken`).
 *
 * @param {object} [config] The axios request config.
 * @returns {(string|null)} The token, or null.
 */
const sentBearer = config => {
    const value = config && config.headers && config.headers.Authorization;
    if (typeof value !== 'string' || !value.startsWith('Bearer ')) return null;
    const token = value.slice('Bearer '.length).trim();
    return token || null;
};

/**
 * Authenticated user client.
 *
 * With the Service Worker, the worker adds the Bearer and `X-API-Key` on the
 * intercepted API base, so this axios instance carries neither (constructed
 * with `withApiKey: false`). In the page runtime (no Service Worker API, e.g.
 * the OneApp's iOS webview), its interceptors add them and do the
 * renew-and-retry-once after a 401 that the worker would otherwise do.
 */
class AuthenticatedOpenIDClient extends BaseOpenIDClient {
    constructor(...args) {
        super(...args);
        // `getAuthHeaders` returns {} whenever the worker is decorating
        // requests, which makes this a no-op there. Callers pass absolute URLs
        // (no `baseURL`), which is what it needs.
        this.instanceAxios.interceptors.request.use(async config => {
            if (config[AUTH_RETRIED]) return config;
            let headers;
            try {
                headers = await getAuthHeaders(config.url);
            } catch (error) {
                throw toAxiosError(error, config);
            }
            if (Object.keys(headers).length === 0) return config;
            return { ...config, headers: { ...config.headers, ...headers } };
        });
        // Only for a Bearer the request interceptor above set, so with the
        // worker (no header on the config) a 401 passes through untouched.
        this.instanceAxios.interceptors.response.use(undefined, async error => {
            const config = error ? error.config : undefined;
            const rejectedToken = sentBearer(config);
            if (!error || !error.response || error.response.status !== 401 || !rejectedToken || config[AUTH_RETRIED]) {
                throw error;
            }
            let headers;
            try {
                headers = await getAuthHeaders(config.url, { rejectedToken });
            } catch (renewError) {
                throw toAxiosError(renewError, config);
            }
            return this.instanceAxios.request({
                ...config,
                [AUTH_RETRIED]: true,
                headers: { ...config.headers, ...headers },
            });
        });
    }
}

const buildFullAuthEndpoint = authEndpoint => {
    const tokenEndpointPath = '/protocol/openid-connect/token';
    if (authEndpoint.includes(tokenEndpointPath)) {
        return authEndpoint;
    }
    return `${authEndpoint}${tokenEndpointPath}`;
};

class OpenIDClient {
    anonymousOpenID = null;

    signUpOpenID = null;

    authenticatedOpenID = null;

    initOpenID = apiConf => {
        const fullAuthEndpoint = buildFullAuthEndpoint(apiConf.authEndpoint);
        this.anonymousOpenID = new AnonymousOpenIDClient(apiConf.anonymousAccess, fullAuthEndpoint);
        this.signUpOpenID = new AnonymousOpenIDClient(apiConf.signUp, fullAuthEndpoint);
        this.authenticatedOpenID = new AuthenticatedOpenIDClient(apiConf.authenticatedAccess, null, {
            withApiKey: false,
        });
    };
}

const instance = new OpenIDClient();

export default instance;
